/* src/contexts/WorkspaceContext.js
 * Workspace context for managing selected workspace
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getUserWorkspaces, pickPrimaryWorkspace } from '../utils/accessControl';
import { workspacesApi } from '../utils/api';

const WorkspaceContext = createContext({});

export function useWorkspace() {
  return useContext(WorkspaceContext);
}

export function WorkspaceProvider({ children }) {
  const { currentUser } = useAuth();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
  const [workspaceDetails, setWorkspaceDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      loadWorkspaces();
    } else {
      setSelectedWorkspaceId(null);
      setAvailableWorkspaces([]);
      setWorkspaceDetails([]);
      setLoading(false);
    }
  }, [currentUser]);

  function storageKey(uid) {
    return `workspace_${uid}`;
  }

  function clearSavedWorkspaceIfStale(uid, workspaces) {
    const key = storageKey(uid);
    const saved = localStorage.getItem(key);
    if (saved && !workspaces.includes(saved)) {
      console.warn('[WORKSPACE CONTEXT] Clearing stale saved workspace:', saved);
      localStorage.removeItem(key);
      return null;
    }
    return saved;
  }

  async function loadWorkspaces() {
    if (!currentUser) return [];

    try {
      setLoading(true);

      const payload = await getUserWorkspaces(currentUser.uid);
      const workspaces = payload.workspaces || [];
      const details = payload.details || [];
      setAvailableWorkspaces(workspaces);
      setWorkspaceDetails(details);

      const savedWorkspaceId = clearSavedWorkspaceIfStale(currentUser.uid, workspaces);
      const nextId = pickPrimaryWorkspace({
        uid: currentUser.uid,
        workspaces,
        details,
        saved: savedWorkspaceId,
      });
      setSelectedWorkspaceId(nextId);
      if (nextId) {
        localStorage.setItem(storageKey(currentUser.uid), nextId);
      }
      return workspaces;
    } catch (error) {
      console.error('[WORKSPACE CONTEXT] Error loading workspaces:', error);
      setSelectedWorkspaceId(currentUser.uid);
      setAvailableWorkspaces([currentUser.uid]);
      setWorkspaceDetails([{ id: currentUser.uid, role: 'owner', name: null, archivedAt: null }]);
      return [currentUser.uid];
    } finally {
      setLoading(false);
    }
  }

  function switchWorkspace(workspaceId, allowedIds) {
    if (!currentUser) return;

    const allowed = allowedIds || availableWorkspaces;
    if (!allowed.includes(workspaceId)) {
      console.warn('[WORKSPACE CONTEXT] Ignored switch to inaccessible workspace:', workspaceId);
      return;
    }

    setSelectedWorkspaceId(workspaceId);
    localStorage.setItem(storageKey(currentUser.uid), workspaceId);
  }

  async function createWorkspace(name) {
    if (!currentUser) return null;
    const data = await workspacesApi.createWorkspace(name);
    const workspaces = await loadWorkspaces();
    if (data.workspaceId) {
      switchWorkspace(data.workspaceId, workspaces);
    }
    return data.workspaceId;
  }

  async function archiveWorkspace(workspaceId) {
    await workspacesApi.archiveWorkspace(workspaceId);
    const workspaces = await loadWorkspaces();
    if (selectedWorkspaceId === workspaceId) {
      switchWorkspace(currentUser.uid, workspaces);
    }
  }

  async function unarchiveWorkspace(workspaceId) {
    await workspacesApi.unarchiveWorkspace(workspaceId);
    await loadWorkspaces();
  }

  const selectedDetail = workspaceDetails.find((d) => d.id === selectedWorkspaceId);
  const isOwner = selectedDetail
    ? selectedDetail.role === 'owner'
    : selectedWorkspaceId === currentUser?.uid;

  const workspaceReady = !loading && !!selectedWorkspaceId;

  const value = {
    selectedWorkspaceId,
    availableWorkspaces,
    workspaceDetails,
    isOwner,
    switchWorkspace,
    createWorkspace,
    archiveWorkspace,
    unarchiveWorkspace,
    loading,
    workspaceReady,
    reloadWorkspaces: loadWorkspaces,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}
